export const slugifyCategory = (label) =>
  label.toLowerCase().replace(/ \/ /g, '-').replace(/ /g, '-');

export const CATEGORIES = [
  { id: 'cs2-stats', label: 'CS2 Stats Trackers', color: 'rgba(86, 156, 214, 0.6)', hoverColor: 'rgb(86, 156, 214)' },
  { id: 'faceit', label: 'FACEIT Finders', color: 'rgba(78, 201, 176, 0.6)', hoverColor: 'rgb(78, 201, 176)' },
  { id: 'inventory', label: 'Inventory Checker', color: 'rgba(212, 165, 116, 0.6)', hoverColor: 'rgb(212, 165, 116)' },
  { id: 'steam-profile', label: 'Steam Profile Checker', color: 'rgba(197, 134, 192, 0.6)', hoverColor: 'rgb(197, 134, 192)' },
  { id: 'vac-ban', label: 'VAC Ban Checker', color: 'rgba(255, 68, 68, 0.6)', hoverColor: 'rgb(255, 68, 68)' },
].map(c => ({ ...c, slug: slugifyCategory(c.label) }));

const CATEGORY_MAP = Object.fromEntries(CATEGORIES.map(c => [c.id, c]));

// Interchangeable mirror sites. Members opt in with `group`; never inferred from
// names. The directory renders a group as one row, primary = first member in
// data order (it gets the row's open link, its desc and the pin).
export const TOOL_GROUPS = {
  faceitfinder: { name: 'FaceitFinder' },
};

function tool(id, name, desc, category, urlTemplateFn, group) {
  const cat = CATEGORY_MAP[category];
  return { id, name, desc, category, urlTemplate: urlTemplateFn, color: cat.color, hoverColor: cat.hoverColor, group };
}

export const ALL_TOOLS = {
  // --- CS2 Stats Trackers ---
  'cs2-stats': tool('cs2-stats', 'CS2 Stats', 'Detailed Player Statistics', 'cs2-stats',
    (steamId) => `https://csst.at/profile/${steamId}`),
  'cswatch': tool('cswatch', 'CSWatch', 'Cheating Analysis and Player Stats', 'cs2-stats',
    (steamId) => `https://cswatch.in/player/${steamId}`),
  'cs-stats': tool('cs-stats', 'CStats.gg', 'Match History & Statistics', 'cs2-stats',
    (steamId) => `https://csstats.gg/player/${steamId}`),
  'leetify': tool('leetify', 'Leetify', 'Performance Analytics', 'cs2-stats',
    (steamId) => `https://leetify.com/app/profile/${steamId}`),
  'cswat-ch': tool('cswat-ch', 'CSWat.ch', 'Player Statistics', 'cs2-stats',
    (steamId) => `https://cswat.ch/stats/${steamId}`),
  'tracker-gg': tool('tracker-gg', 'Tracker.gg', 'Premier Stats & Season Tracking', 'cs2-stats',
    (steamId) => `https://tracker.gg/cs2/profile/steam/${steamId}/overview`),
  'cc-stats': tool('cc-stats', 'CC Stats', 'Anti-Smurf Scout Tool', 'cs2-stats',
    (steamId) => `https://ccstats.net/profiles/${steamId}`),
  'cs2-web-stats': tool('cs2-web-stats', 'CS2 Web Stats', 'FACEIT + Leetify Aggregator', 'cs2-stats',
    (steamId) => `https://cs2ws.com/${steamId}`),
  'steamanalyst-cs2': tool('steamanalyst-cs2', 'SteamAnalyst CS2', 'K/D, ADR & Map Stats', 'cs2-stats',
    (steamId) => `https://www.steamanalyst.com/cs2-stats/${steamId}`),
  'skinflow-cs2': tool('skinflow-cs2', 'Skinflow CS2', 'AI Cheating-Signal Score', 'cs2-stats',
    (steamId) => `https://skinflow.gg/cs2-tracker/${steamId}`),
  // --- FACEIT Finders ---
  'faceitfinder-com': tool('faceitfinder-com', 'FaceitFinder.com', 'ELO & Smurf Detection', 'faceit',
    (steamId) => `https://faceitfinder.com/profile/${steamId}`, 'faceitfinder'),
  'faceitfinder-xyz': tool('faceitfinder-xyz', 'FaceitFinder.xyz', 'Alternative FACEIT Finder', 'faceit',
    (steamId) => `https://faceitfinder.xyz/player/${steamId}`, 'faceitfinder'),
  'faceitfinder-app': tool('faceitfinder-app', 'FaceitFinder.app', 'FACEIT Profile Finder', 'faceit',
    (steamId) => `https://www.faceitfinder.app/?id=${steamId}`, 'faceitfinder'),
  'faceitfinder-io': tool('faceitfinder-io', 'FaceitFinder.io', 'Multi-Game FACEIT Finder', 'faceit',
    (steamId) => `https://faceitfinder.io/?steamid=${steamId}`, 'faceitfinder'),
  'faceit-finder-com': tool('faceit-finder-com', 'Faceit-finder.com', 'FACEIT Profile Finder', 'faceit',
    (steamId) => `https://faceit-finder.com/id/${steamId}`, 'faceitfinder'),
  'faceit-tracker': tool('faceit-tracker', 'Faceit Tracker', 'ELO & Leaderboards', 'faceit',
    (steamId) => `https://faceittracker.net/steam-profile/${steamId}`),
  'faceit-analyser': tool('faceit-analyser', 'Faceit Analyser', 'Match Analyser & Hub Stats', 'faceit',
    (steamId) => `https://faceitanalyser.com/stats/${steamId}/cs2`),
  'faceitaccount': tool('faceitaccount', 'FaceitAccount.com', 'ELO & Achievements', 'faceit',
    (steamId) => `https://faceitaccount.com/profile/?id=${steamId}`),
  'fastmm-faceit': tool('fastmm-faceit', 'FastMM Faceit Finder', 'FACEIT Finder Tool', 'faceit',
    (steamId) => `https://fastmm.win/en/services/find-faceit/${steamId}`),

  // --- Inventory / Skin Value ---
  'skinpock': tool('skinpock', 'Skinpock', 'CS2 Inventory Value', 'inventory',
    (steamId) => `https://www.skinpock.com/inventory/${steamId}?game=cs2`),
  'clash-gg': tool('clash-gg', 'Clash.gg', 'Inventory & Skin Prices', 'inventory',
    (steamId) => `https://inventory.clash.gg/users/${steamId}`),
  'cs2-skin-spy': tool('cs2-skin-spy', 'CS2 Skin Spy', 'Inventory Tracking', 'inventory',
    (steamId) => `https://cs2skinspy.com/?id=${steamId}`),
  'skinsbook': tool('skinsbook', 'SkinsBook', 'Inventory Valuation', 'inventory',
    (steamId) => `https://skinsbook.com/inventory-check/result?&users[0].steam_id=${steamId}&users[0].steam_personaname=Player&game=csgo`),
  'steamanalyst-inv': tool('steamanalyst-inv', 'SteamAnalyst Inventory', 'Value from 15+ Markets', 'inventory',
    (steamId) => `https://www.steamanalyst.com/inventory/${steamId}`),
  'cs2locker': tool('cs2locker', 'CS2Locker', 'CSFloat-Priced Inventory', 'inventory',
    (steamId) => `https://cs2locker.com/inventory-value?steamid=${steamId}`),
  // --- Steam Profile / Leaderboards ---
  'steamdb': tool('steamdb', 'SteamDB', 'Steam Account Statistics', 'steam-profile',
    (steamId) => `https://steamdb.info/calculator/${steamId}/`),
  'steam-calculator': tool('steam-calculator', 'Steam Calculator', 'Account Value and Game Statistics', 'steam-profile',
    (steamId) => `https://steamcalculator.gg/profile/${steamId}`),
  'steam-ladder': tool('steam-ladder', 'Steam Ladder', 'World/Country Rank by Level', 'steam-profile',
    (steamId) => `https://steamladder.com/profile/${steamId}`),
  'steamhistory': tool('steamhistory', 'SteamHistory.net', 'Name & Avatar History', 'steam-profile',
    (steamId) => `https://steamhistory.net/id/${steamId}`),
  'steamid-uk': tool('steamid-uk', 'SteamID.uk', 'Historic Names, Avatars & Alts', 'steam-profile',
    (steamId) => `https://steamid.uk/profile/${steamId}`),
  'steamid-pro': tool('steamid-pro', 'SteamID.pro', 'ID Converter + Ban + Stats', 'steam-profile',
    (steamId) => `https://steamid.pro/lookup/${steamId}`),
  'steamid-io': tool('steamid-io', 'SteamID.io', 'All ID Formats & Vanity', 'steam-profile',
    (steamId) => `https://steamid.io/lookup/${steamId}`),
  'findsteamid': tool('findsteamid', 'FindSteamID', 'Lookup + Ban Check', 'steam-profile',
    (steamId) => `https://findsteamid.com/steamid/${steamId}`),
  'skincade-steamid': tool('skincade-steamid', 'SkinCade SteamID', 'All Formats & Creation Date', 'steam-profile',
    (steamId) => `https://steamidfinder.skincade.com/${steamId}`),

  // --- VAC / Ban / Reputation ---
  'vac-ban-com': tool('vac-ban-com', 'VAC-Ban.com', 'Real-Time CS2 Ban Tracker', 'vac-ban',
    (steamId) => `https://vac-ban.com/player/${steamId}`),
  'vaclist': tool('vaclist', 'VacList', 'VAC Tracking with Notifications', 'vac-ban',
    (steamId) => `https://vaclist.net/account/${steamId}`),
  'bansearch': tool('bansearch', 'BanSearch', 'All-in-One Ban Search', 'vac-ban',
    (steamId) => `https://bansearch.com/${steamId}`),
  'steamid-check': tool('steamid-check', 'Steam ID Check', 'VAC/Ban + Matchmaking Analysis', 'vac-ban',
    (steamId) => `https://steamidcheck.com/profile/${steamId}`),
  'sihrep': tool('sihrep', 'SIHRep', 'Community Reviews & Trust Factor', 'vac-ban',
    (steamId) => `https://sihrep.com/user/${steamId}`),
};

// One directory row per tool, except grouped mirrors collapse into the row of
// their first member: { tool, name, members } (members only for groups).
const directoryRows = (tools) => {
  const rows = [];
  const byGroup = {};
  tools.forEach(t => {
    if (!t.group) return rows.push({ tool: t, name: t.name });
    if (byGroup[t.group]) return byGroup[t.group].members.push(t);
    byGroup[t.group] = { tool: t, name: TOOL_GROUPS[t.group].name, members: [t] };
    rows.push(byGroup[t.group]);
  });
  return rows;
};

export const TOOLS_BY_CATEGORY = CATEGORIES.map(cat => {
  const tools = Object.values(ALL_TOOLS).filter(t => t.category === cat.id);
  return { ...cat, tools, rows: directoryRows(tools) };
});

// Ranked by real clicks: `share` = % of all `tool_interaction` click events
// (Umami, grouped by toolId) — a manual snapshot, re-export to refresh. The UI
// shows "—" for null; never fill these with estimates.
export const MOST_USED_TOOLS = [
  { id: 'cswatch', share: null },
  { id: 'cs2-stats', share: null },
  { id: 'cs-stats', share: null },
  { id: 'leetify', share: null },
  { id: 'cswat-ch', share: null },
  { id: 'skinpock', share: null },
  { id: 'cc-stats', share: null },
  { id: 'tracker-gg', share: null },
  { id: 'faceitfinder-com', share: null },
  { id: 'skinflow-cs2', share: null },
  { id: 'steamanalyst-cs2', share: null },
  { id: 'cs2-web-stats', share: null },
  { id: 'vac-ban-com', share: null },
];

export const TOTAL_TOOL_COUNT = Object.keys(ALL_TOOLS).length;

export const resolveVanityUrl = async (vanityUrl) => {
  try {
    const apiUrl = `/api/steam/ISteamUser/ResolveVanityURL/v0001/?vanityurl=${encodeURIComponent(vanityUrl)}`;

    const response = await fetch(apiUrl);
    const data = await response.json();

    if (data.response && data.response.success === 1) {
      return data.response.steamid;
    }
  } catch (error) {
    console.error('Failed to resolve vanity URL:', error);
  }

  return vanityUrl;
};

export const extractSteamId = async (input) => {
  const trimmed = input.trim();

  const steamIdMatch = trimmed.match(/\b(\d{17})\b/);
  if (steamIdMatch) return steamIdMatch[1];

  const profileIdMatch = trimmed.match(/steamcommunity\.com\/profiles\/(\d{17})/);
  if (profileIdMatch) return profileIdMatch[1];

  const customUrlMatch = trimmed.match(/steamcommunity\.com\/id\/([^\/\s]+)/);
  if (customUrlMatch) {
    const vanityUrl = customUrlMatch[1];
    return await resolveVanityUrl(vanityUrl);
  }

  if (!trimmed.match(/^\d+$/) && !trimmed.includes('/')) {
    if (!/^[a-zA-Z0-9_-]+$/.test(trimmed)) return trimmed;
    return await resolveVanityUrl(trimmed);
  }

  return trimmed;
};
